import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import {
  KPIOptions,
  StaffLayoutStyle,
  SortByOptions,
  SortDirection,
  Threshold,
  AssignmentLayoutStyle,
  ShiftColorOptions,
  RoleAction,
  RolePermissionSubject,
  FeatureName,
} from '@wilson/interfaces';
import { ShiftTimelinePreferredSettingsState } from '@wilson/preferred-settings/state';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { cloneDeep } from 'lodash';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import { AuthorizationModule } from '@wilson/authorization';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { AbilityServiceSignal } from '@casl/angular';

@Component({
  selector: 'wilson-shift-timeline-kpi-setting-popup',
  templateUrl: './shift-timeline-kpi-setting-popup.component.html',
  styleUrls: ['./shift-timeline-kpi-setting-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslatePipe,
    NzSelectModule,
    NzRadioModule,
    FormsModule,
    NzSegmentedModule,
    FeatureFlagsModule,
    AuthorizationModule,
    NzTooltipModule,
  ],
})
export class ShiftTimelineKPISettingPopupComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);

  SortDirection = SortDirection;
  SortByOptions = SortByOptions;
  KPIOptions = KPIOptions;
  StaffLayoutStyle = StaffLayoutStyle;
  AssignmentLayoutStyle = AssignmentLayoutStyle;
  selectedSortDirection!: SortDirection;
  selectedSortByOption!: SortByOptions;
  selectedStaffLayoutStyle!: StaffLayoutStyle;
  selectedAssignmentLayoutStyle!: AssignmentLayoutStyle;
  selectedThreshold!: Threshold;
  selectedKPIs!: KPIOptions[];
  ShiftColorOptions = ShiftColorOptions;
  selectedShiftColorOption: ShiftColorOptions = ShiftColorOptions.ShiftCategory;
  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;
  FeatureName = FeatureName;

  sortOptions!: {
    label: string;
    value: SortDirection;
  }[];

  protected can = inject(AbilityServiceSignal).can;

  ngOnInit(): void {
    this.sortOptions = [
      {
        label: this.translateService.instant(
          'shift_timeline.kpi_setting.sort.ascending',
        ),
        value: SortDirection.Ascending,
      },
      {
        label: this.translateService.instant(
          'shift_timeline.kpi_setting.sort.descending',
        ),
        value: SortDirection.Descending,
      },
    ];
    const userId = this.store.selectSnapshot(AuthState.userId);
    if (userId) {
      const preferredSetting = this.store.selectSnapshot(
        ShiftTimelinePreferredSettingsState.getPreferredSetting(userId),
      );

      this.selectedStaffLayoutStyle = preferredSetting.staffLayout;
      this.selectedAssignmentLayoutStyle = preferredSetting.assignmentLayout;
      this.selectedKPIs = preferredSetting.mainKPIs;
      this.selectedSortDirection = preferredSetting.sortDirection;
      this.selectedSortByOption = preferredSetting.sortBy;
      this.selectedThreshold = cloneDeep(preferredSetting.threshold);
      this.selectedShiftColorOption = preferredSetting.shiftColorOption;
    }
  }
}
